import {
  RTMProviderThresholdsDTO,
} from 'dtos';

export type ThresholdFieldKey = keyof RTMProviderThresholdsDTO;

export type ThresholdOption = {
  value: number;
  label: string;
};

export type ThresholdRowKeys = {
  watch?: ThresholdFieldKey;
  needsReview?: ThresholdFieldKey;
  phoneAlert: ThresholdFieldKey;
};

export type ThresholdRowConfig = {
  title: string;
  description: string;
  inputType: 'segmented' | 'fixedNeedsReview';
  keys: ThresholdRowKeys;
  watchOptions?: ThresholdOption[];
  needsReviewOptions?: ThresholdOption[];
  tone?: 'alert';
};

export type ThresholdSectionConfig = {
  id: string;
  title?: string;
  rows: ThresholdRowConfig[];
};

export const thresholdSections: ThresholdSectionConfig[] = [
  {
    id: 'general',
    rows: [
      {
        title: 'Sudden worsening',
        description: 'Q4 — Needs review when flagged',
        inputType: 'fixedNeedsReview',
        tone: 'alert',
        keys: {
          needsReview: 'suddenWorseningNeedsReviewEnabled',
          phoneAlert: 'suddenWorseningIsPhoneAlertEnabled',
        },
      },
    ],
  },
  {
    id: 'pain',
    title: 'Pain · Q1 PEG-3',
    rows: [
      {
        title: 'Pain score',
        description: 'composite, at or above',
        inputType: 'segmented',
        keys: {
          watch: 'painScoreWatch',
          needsReview: 'painScoreNeedsReview',
          phoneAlert: 'painScoreIsPhoneAlertEnabled',
        },
        watchOptions: [
          { value: 3, label: '3/10' },
          { value: 4, label: '4/10' },
          { value: 5, label: '5/10' },
          { value: 6, label: '6/10' },
          { value: 7, label: '7/10' },
          { value: 8, label: '8/10' },
        ],
        needsReviewOptions: [
          { value: 4, label: '4/10' },
          { value: 5, label: '5/10' },
          { value: 6, label: '6/10' },
          { value: 7, label: '7/10' },
          { value: 8, label: '8/10' },
          { value: 9, label: '9/10' },
        ],
      },
      {
        title: 'Pain spike',
        description: 'increase in one check-in, at or above',
        inputType: 'segmented',
        keys: {
          watch: 'painSpikeWatch',
          needsReview: 'painSpikeNeedsReview',
          phoneAlert: 'painSpikeIsPhoneAlertEnabled',
        },
        watchOptions: [
          { value: 1, label: '+1' },
          { value: 2, label: '+2' },
          { value: 3, label: '+3' },
          { value: 4, label: '+4' },
        ],
        needsReviewOptions: [
          { value: 2, label: '+2' },
          { value: 3, label: '+3' },
          { value: 4, label: '+4' },
          { value: 5, label: '+5' },
        ],
      },
      {
        title: 'Consecutive worsening',
        description: 'check-ins in a row, at or above',
        inputType: 'segmented',
        keys: {
          watch: 'consecutiveWorseningWatch',
          needsReview: 'consecutiveWorseningNeedsReview',
          phoneAlert: 'consecutiveWorseningIsPhoneAlertEnabled',
        },
        watchOptions: [
          { value: 2, label: '2x' },
          { value: 3, label: '3x' },
          { value: 4, label: '4x' },
          { value: 5, label: '5x' },
        ],
        needsReviewOptions: [
          { value: 2, label: '2x' },
          { value: 3, label: '3x' },
          { value: 4, label: '4x' },
          { value: 5, label: '5x' },
          { value: 6, label: '6x' },
        ],
      },
    ],
  },
  {
    id: 'condition',
    title: 'Condition change · Q2',
    rows: [
      {
        title: 'Condition report',
        description: 'at or worse',
        inputType: 'segmented',
        keys: {
          watch: 'conditionReportWatch',
          needsReview: 'conditionReportNeedsReview',
          phoneAlert: 'conditionReportIsPhoneAlertEnabled',
        },
        watchOptions: [
          { value: 4, label: 'About the same' },
          { value: 3, label: 'Slightly worse' },
          { value: 2, label: 'Moderately worse' },
        ],
        needsReviewOptions: [
          { value: 3, label: 'Slightly worse' },
          { value: 2, label: 'Moderately worse' },
          { value: 1, label: 'Much worse' },
        ],
      },
    ],
  },
  {
    id: 'adherence',
    title: 'Adherence · Q3',
    rows: [
      {
        title: 'Care plan adherence',
        description: 'at or worse',
        inputType: 'segmented',
        keys: {
          watch: 'carePlanAdherenceWatch',
          needsReview: 'carePlanAdherenceNeedsReview',
          phoneAlert: 'carePlanAdherenceIsPhoneAlertEnabled',
        },
        watchOptions: [
          { value: 4, label: 'Most of the time' },
          { value: 3, label: 'Sometimes' },
        ],
        needsReviewOptions: [
          { value: 3, label: 'Sometimes' },
          { value: 2, label: 'Rarely' },
          { value: 1, label: 'Never' },
        ],
      },
    ],
  },
];
